import React from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink, TriangleAlert } from "lucide-react";
import { Product } from "@/types/general";
import BaseCollapse from "@reusables/BaseCollapse";
import BaseTable from "@reusables/BaseTable";
import BaseBadge from "@reusables/BaseBadge";
import BaseTooltip from "@reusables/BaseTooltip";
import LocationLabel from "./LocationLabel";
import StockQuantity, { isBelowMinimum } from "./StockQuantity";

type Variant = NonNullable<Product.Extended["variants"]>[number];

interface VariantsLocationsProperties {
    variants: Variant[];
}

/**
 * Read-only overview of the per-location stock of every product variant.
 * Editing of the variants' locations is available only on the variants' own pages.
 */
export default function VariantsLocations(props: VariantsLocationsProperties) {
    const { t } = useTranslation("", { keyPrefix: "products.viewEditProduct.tabs.locations" });
    const history = useHistory();

    return (
        <div className="mt-[40px] space-y-[16px]">
            <div>
                <div className="text-xl text-accent">{t("variants.heading")}</div>
                <div className="text-sm text-[#686868]">{t("variants.description")}</div>
            </div>

            <div className="space-y-[8px]">
                {props.variants.map((variant) => {
                    // `GET product/{id}` always eager-loads `variants.locations`
                    const locations = variant.locations!;
                    const belowMinimumCount = locations.filter((location) =>
                        isBelowMinimum(location.in_stock, location.min_inventory_quantity)
                    ).length;

                    return (
                        <BaseCollapse key={variant.id}>
                            <BaseCollapse.Header className="flex items-center justify-between gap-[16px]">
                                <div className="min-w-0">
                                    <div className="font-semibold truncate text-accent">
                                        {variant.name}
                                    </div>
                                    <div className="text-sm text-[#686868] truncate">
                                        {variant.code}
                                    </div>
                                </div>

                                <div className="flex items-center gap-[16px] shrink-0 text-sm text-[#686868]">
                                    {belowMinimumCount > 0 && (
                                        <BaseBadge
                                            variant="yellow"
                                            icon={<TriangleAlert size={12} />}
                                        >
                                            {t("variants.belowMinimum", {
                                                count: belowMinimumCount,
                                            })}
                                        </BaseBadge>
                                    )}

                                    <span>
                                        {t("variants.locationsCount", { count: locations.length })}
                                    </span>

                                    <span>
                                        {t("variants.inStock")}:{" "}
                                        <span className="font-semibold text-accent">
                                            {variant.in_stock}
                                        </span>
                                    </span>

                                    <BaseTooltip
                                        title={t("variants.openVariant") as string}
                                        placement="top"
                                    >
                                        <button
                                            type="button"
                                            aria-label={t("variants.openVariant")}
                                            // Tailwind's preflight is disabled (see index.css), so a bare <button> keeps the browser's default look
                                            className="flex items-center p-0 border-0 bg-transparent cursor-pointer text-tables-secondaryIcon hover:text-tables-highlightedIcon"
                                            onClick={(e) => {
                                                // Header toggles the collapse on click, and here we only want to navigate
                                                e.stopPropagation();
                                                history.push(
                                                    `/dashboard/products/${variant.id}/details/locations`
                                                );
                                            }}
                                        >
                                            <ExternalLink size={16} />
                                        </button>
                                    </BaseTooltip>

                                    <BaseCollapse.Chevron />
                                </div>
                            </BaseCollapse.Header>

                            <BaseCollapse.Body>
                                <BaseTable
                                    data={locations}
                                    immutable
                                    size="small"
                                    rowKeyGetter={(location) =>
                                        `${location.location_id}-${
                                            location.sub_location_id ?? "store"
                                        }`
                                    }
                                    nothingFound={{
                                        text: t("variants.noLocations") as string,
                                        height: 56,
                                    }}
                                    columns={[
                                        {
                                            header: t("table.columns.0"),
                                            getter: (location) => (
                                                <LocationLabel
                                                    storeName={location.location_name}
                                                    sectionName={location.section_name}
                                                    isDefault={location.is_default}
                                                />
                                            ),
                                        },
                                        {
                                            header: t("table.columns.1"),
                                            getter: (location) => (
                                                <StockQuantity
                                                    inStock={location.in_stock}
                                                    minInventoryQuantity={
                                                        location.min_inventory_quantity
                                                    }
                                                />
                                            ),
                                        },
                                        {
                                            header: t("table.columns.2"),
                                            getter: (location) =>
                                                location.min_inventory_quantity ?? "-",
                                        },
                                    ]}
                                />
                            </BaseCollapse.Body>
                        </BaseCollapse>
                    );
                })}
            </div>
        </div>
    );
}
